import { useEffect, useRef, useContext, useState } from 'react';
import {
  getOriginOverride,
  getOrigin,
  getExperimentSubtags,
  getSource,
} from '@buzzfeed/react-components/lib/utils/affiliate';
import {
  setSubtags,
  isAmazon,
} from '@buzzfeed/bf-utils/lib/affiliate-link';
import ABeagleContext from '../../contexts/abeagle';

/**
 * Added Button’s PostTap prefix to link href
 */
const modifyHREF = ({ element, experiments }) => {
  if (!element) {
    return;
  }

  let href = element.href;

  if(isAmazon(href)) {
    const updates = {
      platform: getSource(),
      origin: getOrigin({ origin: getOriginOverride(element) }),
      experiment: getExperimentSubtags(experiments),
      pubhubId: '0',
      bioId: '0',
      linkId: '0',
      module: '0',
      rank: '0',
      referrer: '0',
    };
    // only overwrite origin if it is nonempty
    if (!updates.origin || updates.origin === '0') {
      delete updates.origin;
    }
    href = setSubtags(href, updates);
  } else {
    // Encode unencoded special characters in param values while preserving
    // existing percent-encoded sequences (e.g., %26 in nested URLs).
    const url = new URL(href);
    const rawSearch = url.search.slice(1);
    if (rawSearch) {
      url.search = rawSearch.split('&').map(pair => {
        const eqIdx = pair.indexOf('=');
        if (eqIdx === -1) return pair;
        const key = pair.slice(0, eqIdx);
        const value = pair.slice(eqIdx + 1);
        const encoded = value.replace(/%[0-9A-Fa-f]{2}|[^A-Za-z0-9\-._~]/g, match =>
          match.length === 3 && match[0] === '%' ? match : encodeURIComponent(match)
        );
        return key + '=' + encoded;
      }).join('&');
    }
    href = url.toString();
  }


  // We're currently unable to bump bf-utils without risking an incident
  // For now, add referrer outside of setSubtags function
  element.href = 'https://r.bttn.io?btn_ref=org-21949126c81417e9&btn_url=' +
    encodeURIComponent(href);
};

/**
 * hrefModifier should be a function that runs at the end of the async modifyHREF function.
 */
export function useButtonPostTap({buzz = {}}) {
  const ref = useRef(null);
  const { experiments, getExperimentValue, getFeatureFlagValue } = useContext(ABeagleContext);
  const [buttonPostTapEnabled, setButtonPostTapEnabled] = useState(false);
  const [buttonPostTapNonAmazonEnabled, setButtonPostTapNonAmazonEnabled] = useState(false);
  const [stateInitialized, setStateInitialized] = useState(false);

  useEffect(() => {
    if (experiments && experiments.loaded) {
      setButtonPostTapEnabled(getExperimentValue('RT-1150-ButtonPostTap-abtest', {
        rejectErrors: false,
      }) === 'control');
      setButtonPostTapNonAmazonEnabled(getFeatureFlagValue('RT-1510-ButtonPostTap-Non-Amazon'));
      setStateInitialized(true);
    }
  }, [experiments, getExperimentValue, getFeatureFlagValue]);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    // Don't process links until experiments are loaded AND state is set
    if (!experiments || !experiments.loaded || !stateInitialized) {
      return;
    }

    element.querySelectorAll('a[data-affiliate]').forEach(link => {
      if (!buttonPostTapEnabled && !buttonPostTapNonAmazonEnabled && isAmazon(link.href)) {
        // Add noskim to all Amazon affiliate links to make sure they are not handled by skimlinks
        link.classList.add('noskim', 'js-buttonposttap-skipped');
        return;
      }

      if (
        (isAmazon(link.href) && !buttonPostTapEnabled) ||
        (!isAmazon(link.href) && !buttonPostTapNonAmazonEnabled)
      ) {
        link.classList.add('js-buttonposttap-skipped');
        return;
      }

      if (link.classList.contains('js-buttonposttap-modified')) { // only modify ButtonPostTaplinks once
        return;
      }

      // prevent handle this link by skimlinks
      link.classList.add('noskim');

      if(!isAmazon(link.href)) {
        asyncModifyHREF(link, experiments);
      } else {
        modifyHREF({ element: link, experiments });
      }
      link.classList.add('js-buttonposttap-modified');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonPostTapEnabled, buttonPostTapNonAmazonEnabled, experiments]);

  return ref;
}

const asyncModifyHREF = (link, experiments) => {
  const callback = (mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-xid' && link.getAttribute('data-xid')) {
        modifyHREF({ element: link, experiments });
        observer.disconnect();
      }
    });
  };

  const observer = new MutationObserver(callback);
  observer.observe(link, { attributes: true });
}
